import styled from "styled-components";
import { FiFileText, FiHelpCircle, FiX, FiEdit } from "react-icons/fi";
import FlexBox from "../FlexBox";
import {
  DAVYS_GREY_200,
  DAVYS_GREY_300,
  DAVYS_GREY_400,
  DAVYS_GREY_800,
} from "../colors";
import useMobileView from "../../../hooks/useMobileView";

const Header = styled(FlexBox)`
  padding: 1.25rem 1rem;
  border-bottom: 1px solid ${DAVYS_GREY_300};
  box-shadow: 0px 0px 24px 0px rgba(98, 69, 132, 0.08);
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const LeftActions = styled(FlexBox)`
  align-items: center;
  gap: 0.75rem;
`;

const RightActions = styled(FlexBox)`
  position: relative;
  align-items: center;
  gap: 0.75rem;

  svg {
    cursor: pointer;
  }
`;

const ActionAnchor = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Tooltip = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: ${({ $isHelp }) => ($isHelp ? "-0.5rem" : "0")};

  width: 12.75rem;
  padding: 0.75rem;

  display: flex;
  flex-direction: column;

  background: rgba(78, 78, 80, 0.97);
  color: white;
  border-radius: 0.75rem;
  z-index: 5;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2);

  &::before {
    content: "";
    position: absolute;
    top: -0.45rem;
    right: 1.1rem;
    width: 0.8rem;
    height: 0.8rem;
    background: rgba(78, 78, 80, 0.97);
    transform: rotate(45deg);
  }
`;

const TooltipText = styled.p`
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.25rem;
`;

const TooltipButton = styled.button`
  margin: 0 !important;
  background: #e9784e;
  color: white;
  border: none;
  border-radius: 999px;
  padding: 0.35rem 0.8rem;

  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
`;

const HeaderButton = styled.button`
  background: white;
  border: 1px solid ${DAVYS_GREY_400};
  border-radius: 0.5rem;
  color: ${DAVYS_GREY_800};
  padding: 0.5rem 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background 0.2s ease;

  &:hover {
    background: ${DAVYS_GREY_200};
  }
`;

const AiChatHeader = ({
  onHistoryClick,
  onNewChatClick,
  showNewChat = false,
  onClose,
  toggleHelp,
  activeTooltip,
  onTooltipDismiss,
}) => {
  const isMobile = useMobileView();

  const newChatTooltipMessage =
    "Click this button to start a new chat. Your current conversation will be saved to Chat History.";

  const helpTooltipMessage =
    "Need help? Click to learn what you can ask and how to use Amaha chat assistant.";

  return (
    <Header>
      <LeftActions>
        <HeaderButton onClick={onHistoryClick}>
          <FiFileText size={16} color={DAVYS_GREY_800} />
          <span>Chat History</span>
        </HeaderButton>
      </LeftActions>

      <RightActions>
        {showNewChat && (
          <ActionAnchor>
            <HeaderButton onClick={onNewChatClick}>
              <FiEdit size={16} color={DAVYS_GREY_800} />
              <span>New Chat</span>
            </HeaderButton>

            {!isMobile && activeTooltip === "new-chat" && (
              <Tooltip>
                <TooltipText>{newChatTooltipMessage}</TooltipText>
                <FlexBox justify="flex-end" margin="0.5rem 0 0">
                  <TooltipButton onClick={onTooltipDismiss}>
                    GOT IT
                  </TooltipButton>
                </FlexBox>
              </Tooltip>
            )}
          </ActionAnchor>
        )}

        {!isMobile && (
          <ActionAnchor>
            <FiHelpCircle
              size={24}
              color={DAVYS_GREY_800}
              onClick={toggleHelp}
            />

            {activeTooltip === "help" && (
              <Tooltip $isHelp>
                <TooltipText>{helpTooltipMessage}</TooltipText>

                <FlexBox justify="flex-end" margin="0.5rem 0 0">
                  <TooltipButton onClick={onTooltipDismiss}>
                    GOT IT
                  </TooltipButton>
                </FlexBox>
              </Tooltip>
            )}
          </ActionAnchor>
        )}

        {isMobile && <FiX size={24} color={DAVYS_GREY_800} onClick={onClose} />}
      </RightActions>
    </Header>
  );
};

export default AiChatHeader;
